import { type FunctionComponent, useMemo } from "react";
import classnames from "classnames";

import { ValueIconName } from "@ppb/the-wall-icons";
import { GenericIcon } from "@ppb/the-wall-icons/GenericIcon/GenericIcon";
import { BetSegmentsIcons, BetSegmentsSize } from "../../BetSegments.types";
import { SelectionSegmentOrientation, type SelectionSegmentProps } from "./SelectionSegment.types";
import { OddsSize } from "@ppb/the-wall-common/types";
import { PNLAndWhatIfSize } from "../../../PNLAndWhatIf/PNLAndWhatIf.types";

import styles from "./SelectionSegment.web.css";
import { Odds } from "@ppb/the-wall-web/components/bricks/Indicators/Odds/Odds";
import { PNLAndWhatIf } from "../../../PNLAndWhatIf/PNLAndWhatIf.web";

export const SelectionSegment: FunctionComponent<SelectionSegmentProps> = ({
  className,
  term,
  value,
  detail,
  rawValue,
  previousValue,
  icon,
  lastElement = false,
  size = BetSegmentsSize.LARGE,
  isOdds,
  isOddsBoosted,
  neutralPnl,
  orientation = SelectionSegmentOrientation.COLUMN,
}) => {
  const isRow = orientation === SelectionSegmentOrientation.ROW;

  const termStyle = classnames(size === BetSegmentsSize.LARGE ? styles.termLarge : styles.term);

  const containerStyles = classnames(styles.container, className, {
    [styles.lastElement]: lastElement,
    [styles.row]: isRow,
  });

  const mappedBetSegmentsIcons: Record<BetSegmentsIcons, ValueIconName> = {
    [BetSegmentsIcons.ODDSBOOST]: ValueIconName.BOOST,
    [BetSegmentsIcons.SUPER_BOOST]: ValueIconName.SUPER_BOOST,
    [BetSegmentsIcons.DOUBLE_UP_BOOST]: ValueIconName.DOUBLE_UP_BOOST,
  };

  const iconName = (icon && mappedBetSegmentsIcons[icon]) || undefined;

  const valueComponent = useMemo(() => {
    if (isOdds) {
      return (
        <Odds
          size={size === BetSegmentsSize.SMALL ? OddsSize.MEDIUM : OddsSize.LARGE}
          icon={iconName ? <GenericIcon name={iconName} /> : undefined}
          value={value}
          detail={detail}
          previousValue={previousValue}
        />
      );
    }
    return (
      <PNLAndWhatIf
        size={size === BetSegmentsSize.SMALL ? PNLAndWhatIfSize.MEDIUM : PNLAndWhatIfSize.LARGE}
        pnl={value}
        rawPnl={neutralPnl ? undefined : rawValue}
        previousPnl={previousValue}
        boosted={!!isOddsBoosted}
      />
    );
  }, [isOdds, size, value, rawValue, previousValue, isOddsBoosted, detail, iconName, neutralPnl]);

  return (
    <dl className={containerStyles}>
      <dt className={termStyle}>{term}</dt>
      <dd className={isRow ? styles.value : undefined}>{valueComponent}</dd>
    </dl>
  );
};
